/**
 * Same-device registration memory (Layer B).
 *
 * On a successful signup we remember the manage token (and the email the user
 * typed) keyed by event id, so a returning visitor on the same browser sees their
 * registered state instead of an empty form. The token is only a pointer — the
 * event page fetches fresh status from it, so nothing here is trusted as truth.
 */

const KEY_PREFIX = 'diveform:registration:';

export interface RememberedRegistration {
  token: string;
  email: string;
}

function keyFor(eventId: number): string {
  return `${KEY_PREFIX}${eventId}`;
}

export function rememberRegistration(eventId: number, data: RememberedRegistration): void {
  try {
    localStorage.setItem(keyFor(eventId), JSON.stringify(data));
  } catch {
    // Storage can be unavailable (private mode, quota); Layer B just degrades to A.
  }
}

export function getRememberedRegistration(eventId: number): RememberedRegistration | null {
  try {
    const raw = localStorage.getItem(keyFor(eventId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RememberedRegistration>;
    if (parsed && typeof parsed.token === 'string') {
      return { token: parsed.token, email: typeof parsed.email === 'string' ? parsed.email : '' };
    }
  } catch {
    // Malformed/unavailable — treat as no memory.
  }
  return null;
}

export function forgetRegistration(eventId: number): void {
  try {
    localStorage.removeItem(keyFor(eventId));
  } catch {
    // Ignore — nothing to clean up if storage is unavailable.
  }
}
